import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { 
  Plus, 
  Trash2, 
  CalendarIcon,
  Loader2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  DollarSign,
  Users,
  Building2,
  FileText,
  CreditCard,
  Clock,
  CheckCircle2,
  XCircle,
  Upload,
  Download,
  Eye,
  Edit,
  Banknote,
  ArrowUpRight,
  ArrowDownRight,
  Scale,
  Printer
} from 'lucide-react';

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700',
  partial: 'bg-amber-100 text-amber-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700'
};

const STATUS_LABELS = {
  open: 'مفتوح',
  partial: 'جزئي',
  paid: 'مدفوع',
  overdue: 'متأخر'
};

const FinancialDuesPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar' || language === 'ku';

  // State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  
  // Receivables state
  const [receivables, setReceivables] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [receivableDialog, setReceivableDialog] = useState(false);
  const [selectedReceivable, setSelectedReceivable] = useState(null);
  const [receivablePaymentDialog, setReceivablePaymentDialog] = useState(false);
  
  // Payables state
  const [payables, setPayables] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [payableDialog, setPayableDialog] = useState(false);
  const [selectedPayable, setSelectedPayable] = useState(null);
  const [payablePaymentDialog, setPayablePaymentDialog] = useState(false);
  
  // Suppliers state
  const [supplierDialog, setSupplierDialog] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  
  // Delete dialogs
  const [deleteReceivableDialog, setDeleteReceivableDialog] = useState(false);
  const [deletePayableDialog, setDeletePayableDialog] = useState(false);
  const [deleteSupplierDialog, setDeleteSupplierDialog] = useState(false);
  
  // Supplier Statement
  const [supplierStatementDialog, setSupplierStatementDialog] = useState(false);
  const [supplierStatement, setSupplierStatement] = useState(null);
  const [statementDates, setStatementDates] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  
  // Custom name options (write name manually)
  const [useCustomCustomer, setUseCustomCustomer] = useState(false);
  const [customCustomerName, setCustomCustomerName] = useState('');
  const [useCustomSupplier, setUseCustomSupplier] = useState(false);
  const [customSupplierName, setCustomSupplierName] = useState('');
  
  // Form state
  const [formLoading, setFormLoading] = useState(false);

  // Fetch data
  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/financial-summary`, { headers: getAuthHeader() });
      setSummary(res.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [API_URL, getAuthHeader]);

  const fetchReceivables = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/receivables`, { headers: getAuthHeader() });
      setReceivables(res.data);
    } catch (error) {
      console.error('Error fetching receivables:', error);
    }
  }, [API_URL, getAuthHeader]);

  const fetchPayables = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/payables`, { headers: getAuthHeader() });
      setPayables(res.data);
    } catch (error) {
      console.error('Error fetching payables:', error);
    }
  }, [API_URL, getAuthHeader]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/suppliers`, { headers: getAuthHeader() });
      setSuppliers(res.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  }, [API_URL, getAuthHeader]);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customers`, { headers: getAuthHeader() });
      setCustomers(res.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, [API_URL, getAuthHeader]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchSummary(),
        fetchReceivables(),
        fetchPayables(),
        fetchSuppliers(),
        fetchCustomers()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [fetchSummary, fetchReceivables, fetchPayables, fetchSuppliers, fetchCustomers]);

  // Format currency
  const fc = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  // ========== RECEIVABLE HANDLERS ==========
  const handleSaveReceivable = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const formData = new FormData(e.target);
    const data = {
      customer_id: useCustomCustomer ? null : formData.get('customer_id'),
      custom_customer_name: useCustomCustomer ? customCustomerName : null,
      description: formData.get('description'),
      amount: parseFloat(formData.get('amount')),
      due_date: formData.get('due_date'),
      invoice_number: formData.get('invoice_number') || '',
      notes: formData.get('notes') || ''
    };

    // Validate
    if (!useCustomCustomer && !data.customer_id) {
      toast.error('اختر الزبون');
      setFormLoading(false);
      return;
    }
    if (useCustomCustomer && !customCustomerName.trim()) {
      toast.error('أدخل اسم العميل');
      setFormLoading(false);
      return;
    }

    try {
      if (selectedReceivable) {
        await axios.put(`${API_URL}/api/receivables/${selectedReceivable.id}`, data, { headers: getAuthHeader() });
        toast.success('تم تحديث المستحق');
      } else {
        await axios.post(`${API_URL}/api/receivables`, data, { headers: getAuthHeader() });
        toast.success('تم إضافة المستحق');
      }
      setReceivableDialog(false);
      setSelectedReceivable(null);
      setUseCustomCustomer(false);
      setCustomCustomerName('');
      fetchReceivables();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteReceivable = async () => {
    try {
      await axios.delete(`${API_URL}/api/receivables/${selectedReceivable.id}`, { headers: getAuthHeader() });
      toast.success('تم حذف المستحق');
      setDeleteReceivableDialog(false);
      setSelectedReceivable(null);
      fetchReceivables();
      fetchSummary();
    } catch (error) {
      toast.error('حدث خطأ في الحذف');
    }
  };

  const handleAddReceivablePayment = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const formData = new FormData(e.target);
    const data = {
      amount: parseFloat(formData.get('amount')),
      date: formData.get('date'),
      payment_method: formData.get('payment_method'),
      notes: formData.get('notes') || ''
    };

    try {
      await axios.post(`${API_URL}/api/receivables/${selectedReceivable.id}/payments`, data, { headers: getAuthHeader() });
      toast.success('تم تسجيل الدفعة');
      setReceivablePaymentDialog(false);
      setSelectedReceivable(null);
      fetchReceivables();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setFormLoading(false);
    }
  };

  // ========== PAYABLE HANDLERS ==========
  const handleSavePayable = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const formData = new FormData(e.target);
    
    // Handle file upload
    const fileInput = e.target.querySelector('input[type="file"]');
    let invoiceFile = null;
    let invoiceFileName = null;
    
    if (fileInput?.files?.[0]) {
      const file = fileInput.files[0];
      invoiceFileName = file.name;
      const reader = new FileReader();
      invoiceFile = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    }

    const data = {
      supplier_id: useCustomSupplier ? null : formData.get('supplier_id'),
      custom_supplier_name: useCustomSupplier ? customSupplierName : null,
      description: formData.get('description'),
      amount: parseFloat(formData.get('amount')),
      due_date: formData.get('due_date'),
      invoice_number: formData.get('invoice_number') || '',
      invoice_file: invoiceFile,
      invoice_file_name: invoiceFileName,
      notes: formData.get('notes') || ''
    };

    // Validate
    if (!useCustomSupplier && !data.supplier_id) {
      toast.error('اختر المورد');
      setFormLoading(false);
      return;
    }
    if (useCustomSupplier && !customSupplierName.trim()) {
      toast.error('أدخل اسم المورد');
      setFormLoading(false);
      return;
    }

    try {
      if (selectedPayable) {
        await axios.put(`${API_URL}/api/payables/${selectedPayable.id}`, data, { headers: getAuthHeader() });
        toast.success('تم تحديث المستحق');
      } else {
        await axios.post(`${API_URL}/api/payables`, data, { headers: getAuthHeader() });
        toast.success('تم إضافة المستحق');
      }
      setPayableDialog(false);
      setSelectedPayable(null);
      setUseCustomSupplier(false);
      setCustomSupplierName('');
      fetchPayables();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeletePayable = async () => {
    try {
      await axios.delete(`${API_URL}/api/payables/${selectedPayable.id}`, { headers: getAuthHeader() });
      toast.success('تم حذف المستحق');
      setDeletePayableDialog(false);
      setSelectedPayable(null);
      fetchPayables();
      fetchSummary();
    } catch (error) {
      toast.error('حدث خطأ في الحذف');
    }
  };

  const handleAddPayablePayment = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const formData = new FormData(e.target);
    const data = {
      amount: parseFloat(formData.get('amount')),
      date: formData.get('date'),
      payment_method: formData.get('payment_method'),
      notes: formData.get('notes') || ''
    };

    try {
      await axios.post(`${API_URL}/api/payables/${selectedPayable.id}/payments`, data, { headers: getAuthHeader() });
      toast.success('تم تسجيل الدفعة');
      setPayablePaymentDialog(false);
      setSelectedPayable(null);
      fetchPayables();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setFormLoading(false);
    }
  };

  // ========== SUPPLIER HANDLERS ==========
  const handleSaveSupplier = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      company_name: formData.get('company_name') || '',
      contact_person: formData.get('contact_person') || '',
      phone: formData.get('phone') || '',
      email: formData.get('email') || '',
      address: formData.get('address') || '',
      tax_number: formData.get('tax_number') || '',
      iban: formData.get('iban') || '',
      bic: formData.get('bic') || '',
      notes: formData.get('notes') || ''
    };

    try {
      if (selectedSupplier) {
        await axios.put(`${API_URL}/api/suppliers/${selectedSupplier.id}`, data, { headers: getAuthHeader() });
        toast.success('تم تحديث المورد');
      } else {
        await axios.post(`${API_URL}/api/suppliers`, data, { headers: getAuthHeader() });
        toast.success('تم إضافة المورد');
      }
      setSupplierDialog(false);
      setSelectedSupplier(null);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteSupplier = async () => {
    try {
      await axios.delete(`${API_URL}/api/suppliers/${selectedSupplier.id}`, { headers: getAuthHeader() });
      toast.success('تم حذف المورد');
      setDeleteSupplierDialog(false);
      setSelectedSupplier(null);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'لا يمكن حذف مورد له مستحقات');
    }
  };

  // ========== SUPPLIER STATEMENT ==========
  const fetchSupplierStatement = async () => {
    if (!selectedSupplier) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/suppliers/${selectedSupplier.id}/statement?start_date=${statementDates.start}&end_date=${statementDates.end}`,
        { headers: getAuthHeader() }
      );
      setSupplierStatement(res.data);
    } catch (error) {
      toast.error('خطأ في تحميل كشف الحساب');
    }
  };

  const openSupplierStatement = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierStatementDialog(true);
  };

  useEffect(() => {
    if (supplierStatementDialog && selectedSupplier) {
      fetchSupplierStatement();
    }
  }, [supplierStatementDialog, selectedSupplier, statementDates]);

  const handlePrintSupplierStatement = () => {
    if (!supplierStatement) return;

    const rows = supplierStatement.payables.map(p => `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">${p.due_date}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">${p.invoice_number || '-'}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">${p.description}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${fc(p.amount)} €</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center;color:#059669">${fc(p.paid_amount)} €</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center;font-weight:bold;color:#dc2626">${fc(p.amount - p.paid_amount)} €</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">
          <span style="padding:2px 8px;border-radius:4px;font-size:11px;background:${p.status === 'paid' ? '#d1fae5' : p.status === 'overdue' ? '#fee2e2' : '#dbeafe'};color:${p.status === 'paid' ? '#065f46' : p.status === 'overdue' ? '#991b1b' : '#1e40af'}">
            ${STATUS_LABELS[p.status]}
          </span>
        </td>
      </tr>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>كشف حساب مورد - ${supplierStatement.supplier.name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; direction: rtl; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #dc2626; color: white; padding: 10px; text-align: right; }
          .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
          .summary { display: flex; gap: 15px; margin: 20px 0; }
          .summary-box { padding: 15px 25px; border-radius: 8px; text-align: center; }
          @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1 style="color:#dc2626;margin:0">كشف حساب مورد</h1>
            <h2 style="margin:5px 0">${supplierStatement.supplier.name}</h2>
            ${supplierStatement.supplier.company_name ? `<p style="color:#666">${supplierStatement.supplier.company_name}</p>` : ''}
          </div>
          <div style="text-align:left">
            <p>الفترة: ${supplierStatement.start_date} — ${supplierStatement.end_date}</p>
            ${supplierStatement.supplier.phone ? `<p>هاتف: ${supplierStatement.supplier.phone}</p>` : ''}
          </div>
        </div>

        <div class="summary">
          <div class="summary-box" style="background:#fee2e2">
            <div style="font-size:12px;color:#991b1b">إجمالي المستحقات</div>
            <div style="font-size:24px;font-weight:bold;color:#dc2626">${fc(supplierStatement.summary.total_amount)} €</div>
          </div>
          <div class="summary-box" style="background:#d1fae5">
            <div style="font-size:12px;color:#065f46">المدفوع</div>
            <div style="font-size:24px;font-weight:bold;color:#059669">${fc(supplierStatement.summary.total_paid)} €</div>
          </div>
          <div class="summary-box" style="background:#fef3c7">
            <div style="font-size:12px;color:#92400e">المتبقي</div>
            <div style="font-size:24px;font-weight:bold;color:#d97706">${fc(supplierStatement.summary.balance)} €</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>تاريخ الاستحقاق</th>
              <th>رقم الفاتورة</th>
              <th>الوصف</th>
              <th>المبلغ</th>
              <th>المدفوع</th>
              <th>المتبقي</th>
              <th>الحالة</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>

        <div style="margin-top:30px;padding:15px;background:#fef3c7;border-radius:8px;text-align:center">
          <span style="font-size:14px">الرصيد المتبقي:</span>
          <span style="font-size:24px;font-weight:bold;margin-right:10px;color:#d97706">${fc(supplierStatement.summary.balance)} €</span>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="financial-dues-page" className="space-y-5" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl md:text-2xl font-bold text-stone-900 flex items-center gap-2">
          <Scale className="w-6 h-6 text-blue-600" />
          المستحقات المالية
        </h1>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="dashboard" className="gap-1">
            <DollarSign className="w-4 h-4" />
            <span className="hidden sm:inline">نظرة عامة</span>
          </TabsTrigger>
          <TabsTrigger value="receivables" className="gap-1">
            <ArrowDownRight className="w-4 h-4 text-green-600" />
            <span className="hidden sm:inline">لنا</span>
          </TabsTrigger>
          <TabsTrigger value="payables" className="gap-1">
            <ArrowUpRight className="w-4 h-4 text-red-600" />
            <span className="hidden sm:inline">علينا</span>
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="gap-1">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">الموردين</span>
          </TabsTrigger>
        </TabsList>

        {/* ========== DASHBOARD TAB ========== */}
        <TabsContent value="dashboard" className="space-y-5 mt-5">
          {summary && (
            <>
              {/* Main Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Receivables Card */}
                <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-green-800">
                      <ArrowDownRight className="w-5 h-5" />
                      مستحقات لنا (Forderungen)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-green-700">{fc(summary.receivables.outstanding)} €</p>
                    <p className="text-sm text-green-600 mt-1">
                      من أصل {fc(summary.receivables.total)} € | مستلم: {fc(summary.receivables.received)} €
                    </p>
                    {summary.receivables.overdue > 0 && (
                      <div className="mt-2 flex items-center gap-1 text-red-600 text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        متأخر: {fc(summary.receivables.overdue)} € ({summary.receivables.overdue_count} فاتورة)
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Payables Card */}
                <Card className="border-red-200 bg-gradient-to-br from-red-50 to-rose-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-red-800">
                      <ArrowUpRight className="w-5 h-5" />
                      مستحقات علينا (Verbindlichkeiten)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-red-700">{fc(summary.payables.outstanding)} €</p>
                    <p className="text-sm text-red-600 mt-1">
                      من أصل {fc(summary.payables.total)} € | مدفوع: {fc(summary.payables.paid)} €
                    </p>
                    {summary.payables.overdue > 0 && (
                      <div className="mt-2 flex items-center gap-1 text-red-600 text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        متأخر: {fc(summary.payables.overdue)} € ({summary.payables.overdue_count} فاتورة)
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Net Position Card */}
                <Card className={`border-2 ${summary.net_position >= 0 ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50' : 'border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50'}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className={`text-base flex items-center gap-2 ${summary.net_position >= 0 ? 'text-blue-800' : 'text-amber-800'}`}>
                      <Scale className="w-5 h-5" />
                      صافي الوضع المالي
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className={`text-3xl font-bold ${summary.net_position >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                      {fc(summary.net_position)} €
                    </p>
                    <p className={`text-sm mt-1 ${summary.net_position >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                      {summary.net_position >= 0 ? 'لصالحنا' : 'علينا'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Aging Analysis */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Receivables Aging */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base text-green-700">تحليل المستحقات لنا حسب تاريخ الاستحقاق</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                      <span className="text-red-700">متأخر</span>
                      <span className="font-bold text-red-700">{fc(summary.receivables.overdue)} €</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-amber-50 rounded">
                      <span className="text-amber-700">خلال 7 أيام</span>
                      <span className="font-bold text-amber-700">{fc(summary.receivables.due_7_days)} €</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                      <span className="text-blue-700">خلال 30 يوم</span>
                      <span className="font-bold text-blue-700">{fc(summary.receivables.due_30_days)} €</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-stone-50 rounded">
                      <span className="text-stone-700">خلال 60 يوم</span>
                      <span className="font-bold text-stone-700">{fc(summary.receivables.due_60_days)} €</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-stone-50 rounded">
                      <span className="text-stone-600">خلال 90 يوم</span>
                      <span className="font-bold text-stone-600">{fc(summary.receivables.due_90_days)} €</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Payables Aging */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base text-red-700">تحليل المستحقات علينا حسب تاريخ الاستحقاق</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                      <span className="text-red-700">متأخر</span>
                      <span className="font-bold text-red-700">{fc(summary.payables.overdue)} €</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-amber-50 rounded">
                      <span className="text-amber-700">خلال 7 أيام</span>
                      <span className="font-bold text-amber-700">{fc(summary.payables.due_7_days)} €</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                      <span className="text-blue-700">خلال 30 يوم</span>
                      <span className="font-bold text-blue-700">{fc(summary.payables.due_30_days)} €</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-stone-50 rounded">
                      <span className="text-stone-700">خلال 60 يوم</span>
                      <span className="font-bold text-stone-700">{fc(summary.payables.due_60_days)} €</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-stone-50 rounded">
                      <span className="text-stone-600">خلال 90 يوم</span>
                      <span className="font-bold text-stone-600">{fc(summary.payables.due_90_days)} €</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Alerts */}
              {summary.total_overdue_count > 0 && (
                <Card className="border-red-300 bg-red-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                      <div>
                        <p className="font-bold text-red-800">تنبيه: يوجد {summary.total_overdue_count} مستحق متأخر!</p>
                        <p className="text-sm text-red-700">
                          {summary.receivables.overdue_count > 0 && `مستحقات لنا متأخرة: ${summary.receivables.overdue_count}`}
                          {summary.receivables.overdue_count > 0 && summary.payables.overdue_count > 0 && ' | '}
                          {summary.payables.overdue_count > 0 && `مستحقات علينا متأخرة: ${summary.payables.overdue_count}`}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ========== RECEIVABLES TAB ========== */}
        <TabsContent value="receivables" className="space-y-4 mt-5">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-green-700 flex items-center gap-2">
              <ArrowDownRight className="w-5 h-5" />
              مستحقات لنا (Forderungen)
            </h2>
            <Button onClick={() => { setSelectedReceivable(null); setReceivableDialog(true); }} className="gap-1 bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4" /> إضافة مستحق
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>الزبون</TableHead>
                    <TableHead>الوصف</TableHead>
                    <TableHead>رقم الفاتورة</TableHead>
                    <TableHead className="text-center">المبلغ</TableHead>
                    <TableHead className="text-center">المدفوع</TableHead>
                    <TableHead className="text-center">المتبقي</TableHead>
                    <TableHead className="text-center">تاريخ الاستحقاق</TableHead>
                    <TableHead className="text-center">الحالة</TableHead>
                    <TableHead className="text-center">إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receivables.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-stone-500">
                        لا توجد مستحقات مسجلة
                      </TableCell>
                    </TableRow>
                  ) : (
                    receivables.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.customer_name}</TableCell>
                        <TableCell>{r.description}</TableCell>
                        <TableCell>{r.invoice_number || '-'}</TableCell>
                        <TableCell className="text-center font-bold">{fc(r.amount)} €</TableCell>
                        <TableCell className="text-center text-green-600">{fc(r.paid_amount)} €</TableCell>
                        <TableCell className="text-center font-bold text-amber-600">{fc(r.amount - r.paid_amount)} €</TableCell>
                        <TableCell className="text-center">{r.due_date}</TableCell>
                        <TableCell className="text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[r.status]}`}>
                            {STATUS_LABELS[r.status]}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-center gap-1">
                            {r.status !== 'paid' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setSelectedReceivable(r); setReceivablePaymentDialog(true); }}
                                className="text-green-600 hover:text-green-700"
                                title="تسجيل دفعة"
                              >
                                <Banknote className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedReceivable(r); setReceivableDialog(true); }}
                              title="تعديل"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedReceivable(r); setDeleteReceivableDialog(true); }}
                              className="text-red-600 hover:text-red-700"
                              title="حذف"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== PAYABLES TAB ========== */}
        <TabsContent value="payables" className="space-y-4 mt-5">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-red-700 flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5" />
              مستحقات علينا (Verbindlichkeiten)
            </h2>
            <Button onClick={() => { setSelectedPayable(null); setPayableDialog(true); }} className="gap-1 bg-red-600 hover:bg-red-700">
              <Plus className="w-4 h-4" /> إضافة مستحق
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>المورد</TableHead>
                    <TableHead>الوصف</TableHead>
                    <TableHead>رقم الفاتورة</TableHead>
                    <TableHead className="text-center">المبلغ</TableHead>
                    <TableHead className="text-center">المدفوع</TableHead>
                    <TableHead className="text-center">المتبقي</TableHead>
                    <TableHead className="text-center">تاريخ الاستحقاق</TableHead>
                    <TableHead className="text-center">الحالة</TableHead>
                    <TableHead className="text-center">ملف</TableHead>
                    <TableHead className="text-center">إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payables.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-stone-500">
                        لا توجد مستحقات مسجلة
                      </TableCell>
                    </TableRow>
                  ) : (
                    payables.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.supplier_name}</TableCell>
                        <TableCell>{p.description}</TableCell>
                        <TableCell>{p.invoice_number || '-'}</TableCell>
                        <TableCell className="text-center font-bold">{fc(p.amount)} €</TableCell>
                        <TableCell className="text-center text-green-600">{fc(p.paid_amount)} €</TableCell>
                        <TableCell className="text-center font-bold text-red-600">{fc(p.amount - p.paid_amount)} €</TableCell>
                        <TableCell className="text-center">{p.due_date}</TableCell>
                        <TableCell className="text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[p.status]}`}>
                            {STATUS_LABELS[p.status]}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          {p.invoice_file ? (
                            <a href={p.invoice_file} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              <FileText className="w-4 h-4 inline" />
                            </a>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-center gap-1">
                            {p.status !== 'paid' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setSelectedPayable(p); setPayablePaymentDialog(true); }}
                                className="text-green-600 hover:text-green-700"
                                title="تسجيل دفعة"
                              >
                                <Banknote className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedPayable(p); setPayableDialog(true); }}
                              title="تعديل"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedPayable(p); setDeletePayableDialog(true); }}
                              className="text-red-600 hover:text-red-700"
                              title="حذف"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== SUPPLIERS TAB ========== */}
        <TabsContent value="suppliers" className="space-y-4 mt-5">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-stone-700 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              الموردين (Lieferanten)
            </h2>
            <Button onClick={() => { setSelectedSupplier(null); setSupplierDialog(true); }} className="gap-1">
              <Plus className="w-4 h-4" /> إضافة مورد
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {suppliers.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="py-8 text-center text-stone-500">
                  لا يوجد موردين مسجلين. أضف مورد جديد للبدء.
                </CardContent>
              </Card>
            ) : (
              suppliers.map((s) => (
                <Card key={s.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-stone-500" />
                        {s.name}
                      </span>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openSupplierStatement(s)} title="كشف الحساب" className="text-blue-600">
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedSupplier(s); setSupplierDialog(true); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { setSelectedSupplier(s); setDeleteSupplierDialog(true); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {s.company_name && <p className="text-stone-600">{s.company_name}</p>}
                    {s.phone && <p className="text-stone-500">{s.phone}</p>}
                    {s.email && <p className="text-stone-500">{s.email}</p>}
                    <div className="pt-2 border-t flex justify-between">
                      <span className="text-stone-600">المستحق:</span>
                      <span className="font-bold text-red-600">{fc(s.balance)} €</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ========== DIALOGS ========== */}

      {/* Receivable Dialog */}
      <Dialog open={receivableDialog} onOpenChange={setReceivableDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedReceivable ? 'تعديل مستحق' : 'إضافة مستحق جديد'} (لنا)</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveReceivable} className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="customCustomerCheck"
                  checked={useCustomCustomer}
                  onChange={(e) => {
                    setUseCustomCustomer(e.target.checked);
                    setCustomCustomerName('');
                  }}
                  className="w-4 h-4"
                />
                <label htmlFor="customCustomerCheck" className="text-sm text-stone-600 cursor-pointer">
                  إدخال اسم عميل يدوياً
                </label>
              </div>
              
              {!useCustomCustomer ? (
                <div>
                  <Label>الزبون *</Label>
                  <Select name="customer_id" defaultValue={selectedReceivable?.customer_id}>
                    <SelectTrigger>
                      <SelectValue placeholder="اختر الزبون" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label>اسم العميل *</Label>
                  <Input 
                    value={customCustomerName}
                    onChange={(e) => setCustomCustomerName(e.target.value)}
                    placeholder="أدخل اسم العميل"
                  />
                </div>
              )}
            </div>
            <div>
              <Label>الوصف *</Label>
              <Input name="description" defaultValue={selectedReceivable?.description} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>المبلغ (€) *</Label>
                <Input name="amount" type="number" step="0.01" defaultValue={selectedReceivable?.amount} required />
              </div>
              <div>
                <Label>تاريخ الاستحقاق *</Label>
                <Input name="due_date" type="date" defaultValue={selectedReceivable?.due_date || format(new Date(), 'yyyy-MM-dd')} required />
              </div>
            </div>
            <div>
              <Label>رقم الفاتورة</Label>
              <Input name="invoice_number" defaultValue={selectedReceivable?.invoice_number} />
            </div>
            <div>
              <Label>ملاحظات</Label>
              <Input name="notes" defaultValue={selectedReceivable?.notes} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReceivableDialog(false)}>إلغاء</Button>
              <Button type="submit" disabled={formLoading} className="bg-green-600 hover:bg-green-700">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'حفظ'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receivable Payment Dialog */}
      <Dialog open={receivablePaymentDialog} onOpenChange={setReceivablePaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>تسجيل دفعة</DialogTitle>
          </DialogHeader>
          {selectedReceivable && (
            <div className="bg-green-50 p-3 rounded-lg mb-4">
              <p className="text-sm text-green-700">المستحق: <strong>{fc(selectedReceivable.amount)} €</strong></p>
              <p className="text-sm text-green-700">المدفوع: <strong>{fc(selectedReceivable.paid_amount)} €</strong></p>
              <p className="text-sm text-green-800 font-bold">المتبقي: {fc(selectedReceivable.amount - selectedReceivable.paid_amount)} €</p>
            </div>
          )}
          <form onSubmit={handleAddReceivablePayment} className="space-y-4">
            <div>
              <Label>المبلغ (€) *</Label>
              <Input 
                name="amount" 
                type="number" 
                step="0.01" 
                max={selectedReceivable ? selectedReceivable.amount - selectedReceivable.paid_amount : undefined}
                required 
              />
            </div>
            <div>
              <Label>التاريخ *</Label>
              <Input name="date" type="date" defaultValue={format(new Date(), 'yyyy-MM-dd')} required />
            </div>
            <div>
              <Label>طريقة الدفع</Label>
              <Select name="payment_method" defaultValue="cash">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">نقداً</SelectItem>
                  <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                  <SelectItem value="check">شيك</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>ملاحظات</Label>
              <Input name="notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReceivablePaymentDialog(false)}>إلغاء</Button>
              <Button type="submit" disabled={formLoading} className="bg-green-600 hover:bg-green-700">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'تسجيل الدفعة'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payable Dialog */}
      <Dialog open={payableDialog} onOpenChange={setPayableDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedPayable ? 'تعديل مستحق' : 'إضافة مستحق جديد'} (علينا)</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSavePayable} className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="customSupplierCheck"
                  checked={useCustomSupplier}
                  onChange={(e) => {
                    setUseCustomSupplier(e.target.checked);
                    setCustomSupplierName('');
                  }}
                  className="w-4 h-4"
                />
                <label htmlFor="customSupplierCheck" className="text-sm text-stone-600 cursor-pointer">
                  إدخال اسم مورد يدوياً
                </label>
              </div>
              
              {!useCustomSupplier ? (
                <div>
                  <Label>المورد *</Label>
                  <Select name="supplier_id" defaultValue={selectedPayable?.supplier_id}>
                    <SelectTrigger>
                      <SelectValue placeholder="اختر المورد" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label>اسم المورد *</Label>
                  <Input 
                    value={customSupplierName}
                    onChange={(e) => setCustomSupplierName(e.target.value)}
                    placeholder="أدخل اسم المورد"
                  />
                </div>
              )}
            </div>
            <div>
              <Label>الوصف *</Label>
              <Input name="description" defaultValue={selectedPayable?.description} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>المبلغ (€) *</Label>
                <Input name="amount" type="number" step="0.01" defaultValue={selectedPayable?.amount} required />
              </div>
              <div>
                <Label>تاريخ الاستحقاق *</Label>
                <Input name="due_date" type="date" defaultValue={selectedPayable?.due_date || format(new Date(), 'yyyy-MM-dd')} required />
              </div>
            </div>
            <div>
              <Label>رقم الفاتورة</Label>
              <Input name="invoice_number" defaultValue={selectedPayable?.invoice_number} />
            </div>
            <div>
              <Label>ملف الفاتورة (PDF أو صورة)</Label>
              <Input type="file" accept=".pdf,.png,.jpg,.jpeg" />
              {selectedPayable?.invoice_file_name && (
                <p className="text-xs text-stone-500 mt-1">الملف الحالي: {selectedPayable.invoice_file_name}</p>
              )}
            </div>
            <div>
              <Label>ملاحظات</Label>
              <Input name="notes" defaultValue={selectedPayable?.notes} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPayableDialog(false)}>إلغاء</Button>
              <Button type="submit" disabled={formLoading} className="bg-red-600 hover:bg-red-700">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'حفظ'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payable Payment Dialog */}
      <Dialog open={payablePaymentDialog} onOpenChange={setPayablePaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>تسجيل دفعة</DialogTitle>
          </DialogHeader>
          {selectedPayable && (
            <div className="bg-red-50 p-3 rounded-lg mb-4">
              <p className="text-sm text-red-700">المستحق: <strong>{fc(selectedPayable.amount)} €</strong></p>
              <p className="text-sm text-red-700">المدفوع: <strong>{fc(selectedPayable.paid_amount)} €</strong></p>
              <p className="text-sm text-red-800 font-bold">المتبقي: {fc(selectedPayable.amount - selectedPayable.paid_amount)} €</p>
            </div>
          )}
          <form onSubmit={handleAddPayablePayment} className="space-y-4">
            <div>
              <Label>المبلغ (€) *</Label>
              <Input 
                name="amount" 
                type="number" 
                step="0.01" 
                max={selectedPayable ? selectedPayable.amount - selectedPayable.paid_amount : undefined}
                required 
              />
            </div>
            <div>
              <Label>التاريخ *</Label>
              <Input name="date" type="date" defaultValue={format(new Date(), 'yyyy-MM-dd')} required />
            </div>
            <div>
              <Label>طريقة الدفع</Label>
              <Select name="payment_method" defaultValue="bank_transfer">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">نقداً</SelectItem>
                  <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                  <SelectItem value="check">شيك</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>ملاحظات</Label>
              <Input name="notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPayablePaymentDialog(false)}>إلغاء</Button>
              <Button type="submit" disabled={formLoading} className="bg-red-600 hover:bg-red-700">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'تسجيل الدفعة'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Supplier Dialog */}
      <Dialog open={supplierDialog} onOpenChange={setSupplierDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedSupplier ? 'تعديل مورد' : 'إضافة مورد جديد'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveSupplier} className="space-y-4">
            <div>
              <Label>اسم المورد *</Label>
              <Input name="name" defaultValue={selectedSupplier?.name} required />
            </div>
            <div>
              <Label>اسم الشركة</Label>
              <Input name="company_name" defaultValue={selectedSupplier?.company_name} />
            </div>
            <div>
              <Label>الشخص المسؤول</Label>
              <Input name="contact_person" defaultValue={selectedSupplier?.contact_person} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>الهاتف</Label>
                <Input name="phone" defaultValue={selectedSupplier?.phone} />
              </div>
              <div>
                <Label>البريد الإلكتروني</Label>
                <Input name="email" type="email" defaultValue={selectedSupplier?.email} />
              </div>
            </div>
            <div>
              <Label>العنوان</Label>
              <Input name="address" defaultValue={selectedSupplier?.address} />
            </div>
            <div>
              <Label>الرقم الضريبي</Label>
              <Input name="tax_number" defaultValue={selectedSupplier?.tax_number} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>IBAN</Label>
                <Input name="iban" defaultValue={selectedSupplier?.iban} />
              </div>
              <div>
                <Label>BIC</Label>
                <Input name="bic" defaultValue={selectedSupplier?.bic} />
              </div>
            </div>
            <div>
              <Label>ملاحظات</Label>
              <Input name="notes" defaultValue={selectedSupplier?.notes} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSupplierDialog(false)}>إلغاء</Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'حفظ'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialogs */}
      <AlertDialog open={deleteReceivableDialog} onOpenChange={setDeleteReceivableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription>
              هل أنت متأكد من حذف هذا المستحق؟ سيتم حذف جميع الدفعات المرتبطة به.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteReceivable} className="bg-red-600 hover:bg-red-700">
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deletePayableDialog} onOpenChange={setDeletePayableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription>
              هل أنت متأكد من حذف هذا المستحق؟ سيتم حذف جميع الدفعات المرتبطة به.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePayable} className="bg-red-600 hover:bg-red-700">
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteSupplierDialog} onOpenChange={setDeleteSupplierDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription>
              هل أنت متأكد من حذف هذا المورد؟ لا يمكن حذف مورد له مستحقات مسجلة.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSupplier} className="bg-red-600 hover:bg-red-700">
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Supplier Statement Dialog */}
      <Dialog open={supplierStatementDialog} onOpenChange={setSupplierStatementDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-red-600" />
                كشف حساب مورد - {selectedSupplier?.name}
              </span>
              <Button variant="outline" size="sm" onClick={handlePrintSupplierStatement} className="gap-1">
                <Printer className="w-4 h-4" /> طباعة
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          {/* Date Filter */}
          <div className="flex gap-3 items-end mb-4">
            <div>
              <Label>من</Label>
              <Input
                type="date"
                value={statementDates.start}
                onChange={(e) => setStatementDates({ ...statementDates, start: e.target.value })}
                className="w-36"
              />
            </div>
            <div>
              <Label>إلى</Label>
              <Input
                type="date"
                value={statementDates.end}
                onChange={(e) => setStatementDates({ ...statementDates, end: e.target.value })}
                className="w-36"
              />
            </div>
            <Button onClick={fetchSupplierStatement} size="sm">تحديث</Button>
          </div>

          {supplierStatement && (
            <>
              {/* Supplier Info */}
              {selectedSupplier?.company_name && (
                <p className="text-stone-600 mb-2">{selectedSupplier.company_name}</p>
              )}
              
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-red-600">إجمالي المستحقات</p>
                  <p className="text-xl font-bold text-red-700">{fc(supplierStatement.summary.total_amount)} €</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-green-600">المدفوع</p>
                  <p className="text-xl font-bold text-green-700">{fc(supplierStatement.summary.total_paid)} €</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-amber-600">المتبقي</p>
                  <p className="text-xl font-bold text-amber-700">{fc(supplierStatement.summary.balance)} €</p>
                </div>
              </div>

              {/* Payables Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>تاريخ الاستحقاق</TableHead>
                    <TableHead>رقم الفاتورة</TableHead>
                    <TableHead>الوصف</TableHead>
                    <TableHead className="text-center">المبلغ</TableHead>
                    <TableHead className="text-center">المدفوع</TableHead>
                    <TableHead className="text-center">المتبقي</TableHead>
                    <TableHead className="text-center">الحالة</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {supplierStatement.payables.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-stone-500">
                        لا توجد مستحقات في هذه الفترة
                      </TableCell>
                    </TableRow>
                  ) : (
                    supplierStatement.payables.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.due_date}</TableCell>
                        <TableCell>{p.invoice_number || '-'}</TableCell>
                        <TableCell>{p.description}</TableCell>
                        <TableCell className="text-center">{fc(p.amount)} €</TableCell>
                        <TableCell className="text-center text-green-600">{fc(p.paid_amount)} €</TableCell>
                        <TableCell className="text-center font-bold text-red-600">{fc(p.amount - p.paid_amount)} €</TableCell>
                        <TableCell className="text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[p.status]}`}>
                            {STATUS_LABELS[p.status]}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialDuesPage;
